<template>
  <div id="trucks-list">
    <div class="truck-list-wrapper">
      <div class="panel-scrollable">
        <v-card
          class="truck-card"
          v-for="(truck, index) in trucks"
          :key="index"
        >
          <div
            class="truck-card-header"
            :class="{ 'truck-card-header-errors': !truck.palletsFit() }"
          >
            <span class="truck-header">{{ truck.toString() }}</span>
            <span class="overlap-error" v-if="!truck.palletsFit()"
              >{{ $t("trucks_list.pallets_overlap") }}!!!</span
            >
            <!-- appear only when pallets with no truck selected -->
            <div
              class="add-pallets-button"
              v-if="
                pallets.filter((e) => {
                  return e.truck_id == null;
                }).length > 0 && user.type != 6
              "
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon flat v-on="on" @click="addPalletsPrompt(truck)">
                    <v-icon color="white">add</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("trucks_list.add_pallets") }}</span>
              </v-tooltip>
            </div>
            <div class="truck-edit-button" v-if="user.type != 6">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon flat v-on="on" @click="editTruck(truck)">
                    <v-icon color="white">more_horiz</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("trucks_list.edit_truck") }}</span>
              </v-tooltip>
            </div>
            <div class="truck-remove-button" v-if="user.type != 6">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon flat v-on="on" @click="removeTruckPrompt(truck)">
                    <v-icon color="white">close</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("trucks_list.remove_truck") }}</span>
              </v-tooltip>
            </div>
          </div>
          <div class="truck-content">
            <div class="truck-top-wrapper">
              <truck-top
                ref="truckTop"
                :truck="truck"
                :pallets="allPallets"
                :orders="getTruckOrders(truck)"
                :divider="dividers[index]"
                @palletSelected="openSelectedPallet"
                @updated="$emit('updated')"
              ></truck-top>
            </div>
            <div class="truck-info">
              <div class="truck-settings">
                {{ $t("trucks_list.description") }}
                <input
                  type="text"
                  v-model="truck.description"
                  @input="saveDescription(truck)"
                />
              </div>
              <div class="truck-settings">
                {{ $t("trucks_list.customers") }} {{ truck.customers }}
              </div>
              <div class="truck-settings">
                {{ $t("trucks_list.orders") }} {{ truck.orders }}
              </div>
              <div class="truck-settings">
                {{ $t("trucks_list.status") }} {{ truckStatus(truck) }}
              </div>
              <div class="row-seperator"></div>
              <div
                class="truck-settings"
                v-if="truck.getProductsCount(1, allPallets) > 0"
              >
                {{ $t("trucks_list.windows") }}:
                {{ truck.getProductsCount(1, allPallets) }}
                {{ $t("trucks_list.units") }},
                {{ truck.getProductsArea(1, allPallets) }} m2,
                {{ truck.getProductsWeight(1, allPallets) }} kg
              </div>
              <div
                class="truck-settings"
                v-if="truck.getProductsCount(2, allPallets) > 0"
              >
                {{ $t("trucks_list.doors") }}:
                {{ truck.getProductsCount(2, allPallets) }}
                {{ $t("trucks_list.units") }},
                {{ truck.getProductsArea(2, allPallets) }} m2,
                {{ truck.getProductsWeight(2, allPallets) }} kg
              </div>
            </div>
            <div class="truck-warning">
              <div class="short-truck" v-if="truck.getLength() > truck.length">
                {{ $t("trucks_list.pallets_do_not_fit") }}:
                {{ truck.length }} mm
              </div>
              <div
                class="too-tall-pallets"
                v-if="truck.getHeight() > truck.height"
              >
                {{ $t("trucks_list.pallets_too_tall") }} {{ truck.height }} mm
              </div>
              <div
                class="pallets-too-heavy"
                v-if="truck.getMaxPalletWeight() > truck.max_pallet_weight"
              >
                {{ $t("trucks_list.pallets_too_heavy") }}
                {{ truck.max_pallet_weight }} kg
              </div>
              <div class="pallet-overlap" v-if="!truck.palletsFit()">
                {{ $t("trucks_list.pallets_overlap") }}
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </div>
    <ConfirmBox ref="confirmBox" @confirmDialog="dialogConfirmed" />
  </div>
</template>

<script>
import TruckTop from "@/components/user/trucks/TruckTop.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import Order from "@/models/Order.js";
export default {
  name: "trucks-list",
  props: {
    trucks: {
      type: Array,
      required: true,
    },
    orders: {
      type: Array,
      required: true,
    },
    pallets: {
      type: Array,
      required: true,
    },
    allPallets: {
      type: Array,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dividers: [],
      statusNames: this.$session.get("statusNames"),
    };
  },
  watch: {
    trucks() {
      this.recalculateDividers();
      this.$store.commit("setLoading", false);
      this.$refs.truckTop.forEach((truckTop) => {
        truckTop.enableEditing();
      });
    },
  },
  mounted() {
    this.$nextTick(function () {
      window.addEventListener("resize", this.recalculateDividers);
      this.recalculateDividers();
    });
  },
  methods: {
    editTruck(truck) {
      this.$emit("edittingTruckSelected", truck);
    },
    removeTruckPrompt(truck) {
      if (truck.status >= 4) {
        alert(this.$t("trucks_list.already_confirmed"));
        return;
      }
      const confirmAction = {
        function: "removeTruck",
        truck: truck,
      };
      this.$refs.confirmBox.openDialog(
        this.$t("trucks_list.confirm_delete"),
        confirmAction
      );
    },
    addPalletsPrompt(truck) {
      if (truck.status >= 4) {
        alert(this.$t("trucks_list.already_confirmed"));
        return;
      }
      const confirmAction = {
        truck: truck,
        function: "addPallets",
      };
      this.$refs.confirmBox.openDialog(
        this.$t("trucks_list.confirm_add"),
        confirmAction
      );
    },
    dialogConfirmed(confirmation) {
      this[confirmation.function](confirmation.truck);
    },
    async removeTruck(truck) {
      await truck.remove();
      this.$emit("updated");
    },
    getTruckOrders(truck) {
      let orders = [];
      this.orders.forEach((order) => {
        if (order.inTruck(truck)) {
          orders.push(order);
        }
      });
      return orders;
    },
    async saveDescription(truck) {
      if (this.user.type == 6) return;
      this.$store.commit("setLoading", true);
      await truck.save();
      this.$store.commit("setLoading", false);
    },
    recalculateDividers() {
      let dividers = [];
      this.trucks.forEach((truck) => {
        const wrapper = document.getElementsByClassName("truck-card")[0];
        if (!wrapper) {
          dividers.push(15);
        } else {
          let divider = truck.getLength() / (wrapper.offsetWidth - 80);
          if (divider < 15) {
            divider = 15;
          }
          dividers.push(divider);
        }
      });
      return (this.dividers = dividers);
    },
    truckStatus(truck) {
      const found = this.statusNames.find((statusName) => {
        return (
          statusName.status == truck.status && statusName.table_name == "trucks"
        );
      });
      if (!found) {
        return truck.status;
      }
      return found.text;
    },
    async addPallets(truck) {
      const additionalPallets = this.pallets.filter((pallet) => {
        return pallet.truck_id == null;
      });
      const truckPallets = truck.getTruckPallets(this.allPallets);
      const newTruckPallets = [...truckPallets, ...additionalPallets];
      const orders = Order.getOrdersFromPallets(newTruckPallets);
      truck.orders = Order.getOrdersString(orders);
      truck.customers = Order.getCustomersString(orders);
      await truck.optimize(newTruckPallets);
      await truck.save();
      this.$emit("addedPallet");
    },
    openSelectedPallet(pallet) {
      this.$emit("palletSelected", pallet);
    },
  },
  components: {
    TruckTop,
    ConfirmBox,
  },
};
</script>

<style scoped>
.truck-card {
  position: relative;
  width: calc(100% - 50px);
  min-height: 540px;
  margin-top: 20px;
  margin-left: 25px;
}
.truck-card-header {
  position: relative;
  width: 100%;
  height: 50px;
  background: #b39ddb;
  padding-left: 10px;
  padding-top: 10px;
}
.truck-card-header-errors {
  background: red;
}
.truck-list-wrapper {
  position: absolute;
  width: 100%;
  height: calc(100% - 50px);
}
.truck-header {
  font-weight: 500;
  font-size: 18px;
}
.truck-content {
  position: absolute;
  top: 50px;
  left: 0px;
  width: 100%;
  height: calc(100% - 50px);
}
.truck-top-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 50%;
}
.truck-remove-button {
  position: absolute;
  right: 0;
  top: 0;
  width: 50px;
  height: 50px;
  background: rgb(255, 50, 50);
}
.add-pallets-button {
  background: #26a69a;
  position: absolute;
  right: 100px;
  width: 50px;
  height: 50px;
  top: 0;
}
.truck-edit-button {
  position: absolute;
  right: 50px;
  width: 50px;
  height: 50px;
  top: 0;
  background: #4a148c;
}
.truck-warning {
  position: absolute;
  bottom: 20px;
  width: 100%;
  font-weight: 800;
  color: #4a148c;
  text-align: center;
}
.truck-info {
  position: absolute;
  width: calc(100% - 20px);
  height: 140px;
  bottom: 140px;
  left: 10px;
}
.truck-settings {
  border-bottom: 1px solid black;
  font-weight: 600;
}
.overlap-error {
  color: white;
  font-size: 18px;
  padding-left: 10px;
}
.row-seperator {
  width: 100%;
  height: 12px;
}
</style>
